import React from "react";
import { useState, useEffect }  from "react";
import { Routes, Route, useNavigate, useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import MainAppBar from '../components/AppBar';
import SubContainer from "./subpages/SubContainer";
import { Container, Typography, Link, Box, Divider, Button, GlobalStyles, Backdrop, CircularProgress } from "@mui/material";
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SubPages from "./subpages/SubPagesEnum";
// import MotionContainer from "./subpages/MotionContainer";
import Home from "./subpages/Home";
import MyModels from "./subpages/MyModels";
import NewModel from "./subpages/NewModel";
import FinancialModelEditor from "./subpages/FinancialModelEditor";
import OperationalModelEditor from "./subpages/OperationalModelEditor";
import FinancialStatements from "./subpages/FinancialStatements";
import Audit from "./subpages/Audit";
import History from "./subpages/History";
import Tools from "./subpages/Tools";
import Compare from "./subpages/Compare";
import CalculationResults from "./subpages/CalculationResults";
import Organization from "./subpages/Organization";
import apiService from "../services/ApiService";
import Dashboard from "./subpages/Dashboard";
import Signup from "./SignUp";
import Settings from "./subpages/Settings";
import MyAccount from "./subpages/MyAccount";
import Contact from "./subpages/Contact";
import AI from "./subpages/AI";
import Library from "./subpages/Library";
import Portfolio from "./subpages/Portfolio";
import { getCookie, setCookie } from "../services/Cookies";
import { TutorialProvider } from "../components/Tutorial";
import { useAppNavigation } from "../hooks/useAppNavigation";
import { ModelLayout, ModelContextProvider, useModelContext } from "../components/Layout";
import ChatPanel, { ChatToggleButton } from "../components/Agent/ChatPanel";
import { AgentContextProvider, SharedChatProvider, useChatOpen, useSetAgentModelInfo, useIsOnAIPage } from "../hooks/useAgentContext";
import { useAppStore } from "../shared/stores/useAppStore";

export interface AppMainProps {
    // subPageInitState: any;
    // title: string;
    // username : string;
    // currentPage: string;
}

type User = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    role_slug: string;
  }

export interface AppMainState {
    errorSnackBarOpen: boolean;
    currentUser?: User;
}

export const SCROLLBAR_STYLES = {
    '*::-webkit-scrollbar': {
        width: '0.4em',
        height: '0.4em',
    },
    '*::-webkit-scrollbar-track': {
        WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '*::-webkit-scrollbar-thumb': {
        backgroundColor: 'slategrey',
        outline: '1px solid slategrey',
        borderRadius: '20px',
    },
} as const;

let easing = [0.6, -0.05, 0.01, 0.99];
const animate = {
  opacity: 1,
  y: 0,
  transition: {
    duration: 0.6,
    ease: easing,
    delay: 0.16,
  },
};

// Module-level globals removed — state now lives in useAppStore.
// Thin shims below delegate to the store so that existing import sites
// continue to work until they are individually migrated.

// Syncs ModelContext → AgentContextProvider for layouts that have their own ModelContextProvider
function AgentModelContextSync() {
    const { model, currentScenario, currentSensitivity } = useModelContext();
    const setAgentModelInfo = useSetAgentModelInfo();

    useEffect(() => {
        if (model?.id) {
            setAgentModelInfo({
                model_id: model.id,
                model_name: model.name,
                scenario_id: currentScenario?.id,
                scenario_name: currentScenario?.name,
                sensitivity_id: currentSensitivity?.id,
                sensitivity_name: currentSensitivity?.name,
            });
        }
        return () => setAgentModelInfo(null);
    }, [model?.id, model?.name, currentScenario?.id, currentScenario?.name, currentSensitivity?.id, currentSensitivity?.name, setAgentModelInfo]);

    return null;
}

// Layout for Operational Model Editor: context provider, sidebar handled internally by OME
function OperationalModelLayout({ entity }: { entity: any }) {
    return (
        <ModelContextProvider initialEntity={entity}>
            <AgentModelContextSync />
            <div className="h-full flex flex-col bg-white overflow-hidden">
                <Outlet />
            </div>
        </ModelContextProvider>
    );
}

function AppContent({ currentEntity, currentUser }: { currentEntity: any; currentUser: any }) {
    const { isChatOpen } = useChatOpen();
    const isOnAIPage = useIsOnAIPage();

    return (
        <div className="flex flex-1 min-h-0 overflow-hidden">
            <div className="flex-1 min-w-0 overflow-auto">
                <TutorialProvider>
                    <Routes>
                        <Route path="/models" element={<MyModels />} />
                        <Route path="/library" element={<Library />} />
                        <Route path="/models/new/:entityId?" element={<NewModel entity={currentEntity}/>} />

                        {/* Operational editor: no sidebar, full width */}
                        <Route element={<OperationalModelLayout entity={currentEntity} />}>
                            <Route path="/models/editor/operational/:entityId?" element={<OperationalModelEditor />} />
                        </Route>

                        {/* Model-related routes with persistent sidebar */}
                        <Route element={<ModelLayout />}>
                            <Route path="/models/editor/financial/:entityId?" element={<FinancialModelEditor entity={currentEntity}/>} />
                            <Route path="/financial-statements/:entityId?" element={<FinancialStatements />} />
                            <Route path="/dashboard/:entityId?" element={<Dashboard />} />
                            <Route path="/audit/:entityId?" element={<Audit />} />
                            <Route path="/history/:entityId?" element={<History />} />
                            <Route path="/tools/:entityId?" element={<Tools />} />
                            <Route path="/compare/:entityId?" element={<Compare />} />
                            <Route path="/calculation-results/:entityId?" element={<CalculationResults />} />
                            <Route path="/ai/:entityId?" element={<AI />} />
                        </Route>

                        <Route path="/organization/:entityId?" element={<Organization entity={currentEntity} user={currentUser}/>} />
                        <Route path="/settings/:entityId?" element={<Settings entity={currentEntity}/>} />
                        <Route path="/account/:entityId?" element={<MyAccount entity={currentEntity}/>} />
                        <Route path="/contact" element={<Contact/>} />
                        <Route path="/portfolio" element={<Portfolio />} />
                        {/* Default redirect to models */}
                        <Route path="*" element={<MyModels />} />
                    </Routes>
                </TutorialProvider>
            </div>
            {isChatOpen && !isOnAIPage && <ChatPanel />}
        </div>
    );
}

function GlobalBackDrop() {
    const backdropOpen = useAppStore((s) => s.backdropOpen);

    return (
        <Backdrop
            sx={{ color: '#EEE', zIndex: 1500 }}
            open={backdropOpen}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    );
}


const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
  ) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

function AppMain ({ }) {
    const navigateToPage = useAppNavigation();
    const location = useLocation();

    // Read store state
    const shouldInit = useAppStore((s) => s.shouldInit);
    const errorSnackBarOpen = useAppStore((s) => s.errorSnackBarOpen);
    const forbiddenSnackBarOpen = useAppStore((s) => s.forbiddenSnackBarOpen);
    const closeErrorSnackBar = useAppStore((s) => s.closeErrorSnackBar);
    const closeForbiddenSnackBar = useAppStore((s) => s.closeForbiddenSnackBar);

    // Get entity from location state (passed via navigate) or from cookie as fallback
    const currentEntity = location.state?.entity || (getCookie("entity") && getCookie("entity") !== "" && getCookie("entity") !== 'undefined' ? JSON.parse(getCookie("entity")) : null);

    const [currentUser, setCurrentUser] = React.useState(null);
    const [mounted, setMounted] = React.useState(false);

    // Store entity in cookie when it changes (for page refresh persistence)
    React.useEffect(() => {
        if (currentEntity) {
            setCookie('entity', JSON.stringify(currentEntity));
        }
    }, [currentEntity]);

    React.useEffect(() => {
        const fetchDatas = async() => {
            const store = useAppStore.getState();
            await Promise.all([
                apiService.getCurrentUser().then(async(response) => {
                    if (response) {
                        const elem = await response.json();
                        const user = {
                            id: elem.id,
                            first_name: elem.first_name,
                            last_name: elem.last_name,
                            email: elem.email,
                            role_slug: elem.role_slug,
                            tutorial_advancement: elem.tutorial_advancement,
                        };
                        setCurrentUser(user);
                        store.setCurrentUser(user);
                    }
                }),

                apiService.getUserPreferences().then(async(response) => {
                    if (response) {
                        const resp = await response.json()

                        store.setUserSettings({
                            dateSystem: resp.date_system,
                            roundPrecision: resp.round_precision,
                            numberFormattingLocale: resp.number_formatting_locale,
                            chartPrimaryColor: resp.chart_primary_color ?? null,
                        });
                    }
                }),

                apiService.getUserCommercialPlan().then(async(response) => {
                    if (response && response.ok) {
                        const resp = await response.json()

                        store.setCommercialPlan(resp);
                    }
                }),

                apiService.getErpConnections().then(async(response) => {
                    if (response && response.ok) {
                        const resp = await response.json()
                        store.setHasErpConnections(Array.isArray(resp) && resp.length > 0);
                    }
                }).catch(() => {
                    store.setHasErpConnections(false);
                })
            ]);
            setMounted(!mounted);
        }
        fetchDatas();
    }, [shouldInit])

    return (
        <AgentContextProvider>
        <SharedChatProvider>
        <div className="h-screen flex flex-col overflow-hidden">
            <GlobalStyles styles={SCROLLBAR_STYLES} />
            <MainAppBar />
            <AppContent currentEntity={currentEntity} currentUser={currentUser} />
            <Snackbar open={errorSnackBarOpen} autoHideDuration={6000} onClose={closeErrorSnackBar}>
                <Alert onClose={closeErrorSnackBar} severity="warning" sx={{ width: '100%' }}>
                    An error occurred, we will be fixing it soon
                </Alert>
            </Snackbar>
            <Snackbar open={forbiddenSnackBarOpen} autoHideDuration={6000} onClose={closeForbiddenSnackBar}>
                <Alert onClose={closeForbiddenSnackBar} severity="error" sx={{ width: '100%' }}>
                    You don't have the required permissions to perform this action. Please reach out to the owner of this resource to request access.
                </Alert>
            </Snackbar>
            <GlobalBackDrop/>
            <ChatToggleButton />
        </div>
        </SharedChatProvider>
        </AgentContextProvider>
    )
}


export default AppMain